import React, { useState, useMemo } from 'react';
import { Calculator, TrendingUp, TrendingDown, DollarSign, Percent, Target, Shield, AlertTriangle } from 'lucide-react';
import '../styles/PnLCalculator.css';

function PnLCalculator() {
  const [mode, setMode] = useState('pnl'); // 'pnl', 'position', 'rr'
  const [inputs, setInputs] = useState({
    // Basic PnL
    entryPrice: '',
    exitPrice: '',
    positionSize: '',
    leverage: '1',
    side: 'long',
    feeRate: '0.05',
    // Position Sizing
    accountBalance: '',
    riskPercent: '2',
    stopLossPrice: '',
    // Risk/Reward
    tp1Price: '',
    tp2Price: '',
    tp3Price: '',
    tp1Percent: '50',
    tp2Percent: '30',
    tp3Percent: '20',
  });

  // PnL Calculations
  const pnlCalc = useMemo(() => {
    const entry = parseFloat(inputs.entryPrice) || 0;
    const exit = parseFloat(inputs.exitPrice) || 0;
    const size = parseFloat(inputs.positionSize) || 0;
    const leverage = parseFloat(inputs.leverage) || 1;
    const feeRate = parseFloat(inputs.feeRate) / 100 || 0;
    
    if (entry === 0 || exit === 0 || size === 0) return null;
    
    const isLong = inputs.side === 'long';
    const priceChange = ((exit - entry) / entry) * 100;
    const rawPnL = isLong 
      ? (exit - entry) / entry * size * leverage
      : (entry - exit) / entry * size * leverage;
    
    const entryFee = size * feeRate;
    const exitValue = size + rawPnL;
    const exitFee = Math.abs(exitValue) * feeRate;
    const totalFees = entryFee + exitFee;
    const netPnL = rawPnL - totalFees;
    
    const margin = size / leverage;
    const roi = (netPnL / margin) * 100;
    
    const liquidationPrice = isLong
      ? entry * (1 - 1 / leverage + 0.005)
      : entry * (1 + 1 / leverage - 0.005);
    
    const riskAmount = margin;
    const rewardRatio = netPnL > 0 ? netPnL / riskAmount : 0;
    
    return {
      priceChange: priceChange.toFixed(2),
      rawPnL: rawPnL.toFixed(2),
      totalFees: totalFees.toFixed(2),
      netPnL: netPnL.toFixed(2),
      roi: roi.toFixed(2),
      margin: margin.toFixed(2),
      liquidationPrice: liquidationPrice.toFixed(2),
      rewardRatio: rewardRatio.toFixed(2),
      isProfitable: netPnL >= 0
    };
  }, [inputs]);

  // Position Sizing Calculations
  const positionCalc = useMemo(() => {
    const balance = parseFloat(inputs.accountBalance) || 0;
    const riskPct = parseFloat(inputs.riskPercent) / 100 || 0;
    const entry = parseFloat(inputs.entryPrice) || 0;
    const sl = parseFloat(inputs.stopLossPrice) || 0;
    const leverage = parseFloat(inputs.leverage) || 1;
    
    if (balance === 0 || entry === 0 || sl === 0) return null;
    
    const isLong = inputs.side === 'long';
    const riskAmount = balance * riskPct;
    const slDistance = isLong ? (entry - sl) / entry : (sl - entry) / entry;
    
    if (slDistance <= 0) return null;
    
    const positionSize = riskAmount / slDistance;
    const marginRequired = positionSize / leverage;
    const contracts = positionSize / entry;
    
    const effectiveRisk = (marginRequired / balance) * 100;
    
    return {
      riskAmount: riskAmount.toFixed(2),
      positionSize: positionSize.toFixed(2),
      marginRequired: marginRequired.toFixed(2),
      contracts: contracts.toFixed(6),
      slPercent: (slDistance * 100).toFixed(2),
      effectiveRisk: effectiveRisk.toFixed(2),
      maxLossAmount: riskAmount.toFixed(2)
    };
  }, [inputs]);

  // Risk/Reward Calculations
  const rrCalc = useMemo(() => {
    const entry = parseFloat(inputs.entryPrice) || 0;
    const sl = parseFloat(inputs.stopLossPrice) || 0;
    const tp1 = parseFloat(inputs.tp1Price) || 0;
    const tp2 = parseFloat(inputs.tp2Price) || 0;
    const tp3 = parseFloat(inputs.tp3Price) || 0;
    const size = parseFloat(inputs.positionSize) || 0;
    const leverage = parseFloat(inputs.leverage) || 1;
    
    const tp1Pct = parseFloat(inputs.tp1Percent) / 100 || 0;
    const tp2Pct = parseFloat(inputs.tp2Percent) / 100 || 0;
    const tp3Pct = parseFloat(inputs.tp3Percent) / 100 || 0;
    
    if (entry === 0 || sl === 0) return null;
    
    const isLong = inputs.side === 'long';
    const riskDist = isLong ? (entry - sl) / entry : (sl - entry) / entry;
    
    if (riskDist <= 0) return null;
    
    const targets = [];
    const tpData = [
      { price: tp1, pct: tp1Pct, label: 'TP1' },
      { price: tp2, pct: tp2Pct, label: 'TP2' },
      { price: tp3, pct: tp3Pct, label: 'TP3' }
    ];
    
    tpData.forEach(({ price, pct, label }) => {
      if (price > 0) {
        const rewardDist = isLong ? (price - entry) / entry : (entry - price) / entry;
        const rr = rewardDist / riskDist;
        const pnl = size > 0 ? (rewardDist * size * leverage * pct) : 0;
        targets.push({
          label,
          price: price.toFixed(2),
          rr: rr.toFixed(2),
          pnl: pnl.toFixed(2),
          percent: (pct * 100).toFixed(0),
          rewardPercent: (rewardDist * 100).toFixed(2)
        });
      }
    });
    
    const totalPnl = targets.reduce((sum, t) => sum + parseFloat(t.pnl), 0);
    const avgRR = targets.length > 0 
      ? targets.reduce((sum, t) => sum + parseFloat(t.rr) * (parseFloat(t.percent) / 100), 0) 
      : 0;
    
    const maxLoss = size > 0 ? (riskDist * size * leverage) : 0;
    
    return {
      targets,
      riskPercent: (riskDist * 100).toFixed(2),
      maxLoss: maxLoss.toFixed(2),
      totalPnl: totalPnl.toFixed(2),
      avgRR: avgRR.toFixed(2)
    };
  }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const presetLeverages = [1, 2, 5, 10, 20, 50];

  return (
    <div className="pnl-calculator">
      <div className="calc-header">
        <div className="header-title">
          <Calculator size={20} />
          <h2>Trading Calculator</h2>
        </div>
        <p className="header-subtitle">Professional tools for position planning</p>
      </div>

      {/* Mode Tabs */}
      <div className="calc-tabs">
        <button 
          className={`calc-tab ${mode === 'pnl' ? 'active' : ''}`}
          onClick={() => setMode('pnl')}
        >
          <DollarSign size={14} />
          PnL Calculator
        </button>
        <button 
          className={`calc-tab ${mode === 'position' ? 'active' : ''}`}
          onClick={() => setMode('position')}
        >
          <Shield size={14} />
          Position Sizing
        </button>
        <button 
          className={`calc-tab ${mode === 'rr' ? 'active' : ''}`}
          onClick={() => setMode('rr')}
        >
          <Target size={14} />
          Risk/Reward
        </button>
      </div>

      <div className="calc-content">
        {/* ======== PNL CALCULATOR MODE ======== */}
        {mode === 'pnl' && (
          <>
            <div className="input-section">
              <div className="side-toggle">
                <button 
                  className={`side-btn ${inputs.side === 'long' ? 'active long' : ''}`}
                  onClick={() => handleInputChange('side', 'long')}
                >
                  <TrendingUp size={16} />
                  Long
                </button>
                <button 
                  className={`side-btn ${inputs.side === 'short' ? 'active short' : ''}`}
                  onClick={() => handleInputChange('side', 'short')}
                >
                  <TrendingDown size={16} />
                  Short
                </button>
              </div>

              <div className="input-grid">
                <div className="input-group">
                  <label>Entry Price</label>
                  <div className="input-wrapper">
                    <DollarSign size={14} />
                    <input
                      type="number"
                      placeholder="0.00"
                      value={inputs.entryPrice}
                      onChange={(e) => handleInputChange('entryPrice', e.target.value)}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label>Exit Price (Target)</label>
                  <div className="input-wrapper">
                    <DollarSign size={14} />
                    <input
                      type="number"
                      placeholder="0.00"
                      value={inputs.exitPrice}
                      onChange={(e) => handleInputChange('exitPrice', e.target.value)}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label>Position Size (USDT)</label>
                  <div className="input-wrapper">
                    <DollarSign size={14} />
                    <input
                      type="number"
                      placeholder="0.00"
                      value={inputs.positionSize}
                      onChange={(e) => handleInputChange('positionSize', e.target.value)}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label>Trading Fee (%)</label>
                  <div className="input-wrapper">
                    <Percent size={14} />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.05"
                      value={inputs.feeRate}
                      onChange={(e) => handleInputChange('feeRate', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="leverage-section">
                <label>Leverage</label>
                <div className="leverage-presets">
                  {presetLeverages.map(lev => (
                    <button
                      key={lev}
                      className={`leverage-btn ${inputs.leverage === String(lev) ? 'active' : ''}`}
                      onClick={() => handleInputChange('leverage', String(lev))}
                    >
                      {lev}x
                    </button>
                  ))}
                </div>
                <div className="leverage-input">
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={inputs.leverage}
                    onChange={(e) => handleInputChange('leverage', e.target.value)}
                  />
                  <span className="leverage-value">{inputs.leverage}x</span>
                </div>
              </div>
            </div>

            <div className="results-section">
              {pnlCalc ? (
                <>
                  <div className={`pnl-result ${pnlCalc.isProfitable ? 'profit' : 'loss'}`}>
                    <span className="result-label">Net Profit/Loss</span>
                    <span className="result-value">
                      {pnlCalc.isProfitable ? '+' : ''}{pnlCalc.netPnL} USDT
                    </span>
                    <span className="result-roi">
                      ROI: {pnlCalc.isProfitable ? '+' : ''}{pnlCalc.roi}%
                    </span>
                  </div>

                  <div className="results-grid">
                    <div className="result-item">
                      <span className="label">Price Change</span>
                      <span className={`value ${parseFloat(pnlCalc.priceChange) >= 0 ? 'positive' : 'negative'}`}>
                        {parseFloat(pnlCalc.priceChange) >= 0 ? '+' : ''}{pnlCalc.priceChange}%
                      </span>
                    </div>
                    <div className="result-item">
                      <span className="label">Gross P&L</span>
                      <span className={`value ${parseFloat(pnlCalc.rawPnL) >= 0 ? 'positive' : 'negative'}`}>
                        {parseFloat(pnlCalc.rawPnL) >= 0 ? '+' : ''}{pnlCalc.rawPnL} USDT
                      </span>
                    </div>
                    <div className="result-item">
                      <span className="label">Total Fees</span>
                      <span className="value negative">-{pnlCalc.totalFees} USDT</span>
                    </div>
                    <div className="result-item">
                      <span className="label">Required Margin</span>
                      <span className="value">{pnlCalc.margin} USDT</span>
                    </div>
                    <div className="result-item">
                      <span className="label">Liquidation Price</span>
                      <span className="value warning">${pnlCalc.liquidationPrice}</span>
                    </div>
                    <div className="result-item">
                      <span className="label">Risk/Reward</span>
                      <span className="value">{pnlCalc.rewardRatio}x</span>
                    </div>
                  </div>

                  {parseFloat(inputs.leverage) >= 20 && (
                    <div className="risk-warning">
                      <AlertTriangle size={14} />
                      <p>High leverage ({inputs.leverage}x) significantly increases liquidation risk</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-results">
                  <Calculator size={48} />
                  <p>Enter trade parameters to calculate potential P&L</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* ======== POSITION SIZING MODE ======== */}
        {mode === 'position' && (
          <>
            <div className="input-section">
              <div className="side-toggle">
                <button 
                  className={`side-btn ${inputs.side === 'long' ? 'active long' : ''}`}
                  onClick={() => handleInputChange('side', 'long')}
                >
                  <TrendingUp size={16} />
                  Long
                </button>
                <button 
                  className={`side-btn ${inputs.side === 'short' ? 'active short' : ''}`}
                  onClick={() => handleInputChange('side', 'short')}
                >
                  <TrendingDown size={16} />
                  Short
                </button>
              </div>

              <div className="input-grid">
                <div className="input-group">
                  <label>Account Balance (USDT)</label>
                  <div className="input-wrapper">
                    <DollarSign size={14} />
                    <input
                      type="number"
                      placeholder="1000.00"
                      value={inputs.accountBalance}
                      onChange={(e) => handleInputChange('accountBalance', e.target.value)}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label>Risk Per Trade (%)</label>
                  <div className="input-wrapper">
                    <Percent size={14} />
                    <input
                      type="number"
                      placeholder="2"
                      value={inputs.riskPercent}
                      onChange={(e) => handleInputChange('riskPercent', e.target.value)}
                    />
                  </div>
                  <div className="risk-presets">
                    {[1, 2, 3, 5].map(r => (
                      <button
                        key={r}
                        className={`risk-preset ${inputs.riskPercent === String(r) ? 'active' : ''}`}
                        onClick={() => handleInputChange('riskPercent', String(r))}
                      >
                        {r}%
                      </button>
                    ))}
                  </div>
                </div>

                <div className="input-group">
                  <label>Entry Price</label>
                  <div className="input-wrapper">
                    <DollarSign size={14} />
                    <input
                      type="number"
                      placeholder="0.00"
                      value={inputs.entryPrice}
                      onChange={(e) => handleInputChange('entryPrice', e.target.value)}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label>Stop Loss Price</label>
                  <div className="input-wrapper">
                    <DollarSign size={14} />
                    <input
                      type="number"
                      placeholder="0.00"
                      value={inputs.stopLossPrice}
                      onChange={(e) => handleInputChange('stopLossPrice', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="leverage-section">
                <label>Leverage</label>
                <div className="leverage-presets">
                  {presetLeverages.map(lev => (
                    <button
                      key={lev}
                      className={`leverage-btn ${inputs.leverage === String(lev) ? 'active' : ''}`}
                      onClick={() => handleInputChange('leverage', String(lev))}
                    >
                      {lev}x
                    </button>
                  ))}
                </div>
                <div className="leverage-input">
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={inputs.leverage}
                    onChange={(e) => handleInputChange('leverage', e.target.value)}
                  />
                  <span className="leverage-value">{inputs.leverage}x</span>
                </div>
              </div>
            </div>

            <div className="results-section">
              {positionCalc ? (
                <>
                  <div className="pnl-result profit">
                    <span className="result-label">Recommended Position Size</span>
                    <span className="result-value">{positionCalc.positionSize} USDT</span>
                    <span className="result-roi">{positionCalc.contracts} contracts</span>
                  </div>

                  <div className="results-grid">
                    <div className="result-item highlight">
                      <span className="label">Risk Amount</span>
                      <span className="value warning">${positionCalc.riskAmount}</span>
                    </div>
                    <div className="result-item">
                      <span className="label">Margin Required</span>
                      <span className="value">{positionCalc.marginRequired} USDT</span>
                    </div>
                    <div className="result-item">
                      <span className="label">Stop Loss Distance</span>
                      <span className="value negative">{positionCalc.slPercent}%</span>
                    </div>
                    <div className="result-item">
                      <span className="label">Margin % of Balance</span>
                      <span className="value">{positionCalc.effectiveRisk}%</span>
                    </div>
                  </div>

                  <div className="position-info">
                    <Shield size={14} />
                    <p>This position size risks exactly <strong>{inputs.riskPercent}%</strong> of your account if stop loss is hit.</p>
                  </div>
                </>
              ) : (
                <div className="no-results">
                  <Shield size={48} />
                  <p>Enter your account balance, risk %, entry and stop loss to calculate position size</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* ======== RISK/REWARD MODE ======== */}
        {mode === 'rr' && (
          <>
            <div className="input-section">
              <div className="side-toggle">
                <button 
                  className={`side-btn ${inputs.side === 'long' ? 'active long' : ''}`}
                  onClick={() => handleInputChange('side', 'long')}
                >
                  <TrendingUp size={16} />
                  Long
                </button>
                <button 
                  className={`side-btn ${inputs.side === 'short' ? 'active short' : ''}`}
                  onClick={() => handleInputChange('side', 'short')}
                >
                  <TrendingDown size={16} />
                  Short
                </button>
              </div>

              <div className="input-grid">
                <div className="input-group">
                  <label>Entry Price</label>
                  <div className="input-wrapper">
                    <DollarSign size={14} />
                    <input
                      type="number"
                      placeholder="0.00"
                      value={inputs.entryPrice}
                      onChange={(e) => handleInputChange('entryPrice', e.target.value)}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label>Stop Loss Price</label>
                  <div className="input-wrapper">
                    <DollarSign size={14} />
                    <input
                      type="number"
                      placeholder="0.00"
                      value={inputs.stopLossPrice}
                      onChange={(e) => handleInputChange('stopLossPrice', e.target.value)}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label>Position Size (Optional)</label>
                  <div className="input-wrapper">
                    <DollarSign size={14} />
                    <input
                      type="number"
                      placeholder="For P&L calc"
                      value={inputs.positionSize}
                      onChange={(e) => handleInputChange('positionSize', e.target.value)}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label>Leverage</label>
                  <div className="input-wrapper leverage-compact">
                    <input
                      type="number"
                      min="1"
                      max="100"
                      placeholder="1"
                      value={inputs.leverage}
                      onChange={(e) => handleInputChange('leverage', e.target.value)}
                    />
                    <span>x</span>
                  </div>
                </div>
              </div>

              <div className="tp-section">
                <label className="section-label">Take Profit Levels</label>
                
                <div className="tp-row">
                  <div className="tp-input">
                    <span className="tp-label">TP1</span>
                    <input
                      type="number"
                      placeholder="Price"
                      value={inputs.tp1Price}
                      onChange={(e) => handleInputChange('tp1Price', e.target.value)}
                    />
                  </div>
                  <div className="tp-percent">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={inputs.tp1Percent}
                      onChange={(e) => handleInputChange('tp1Percent', e.target.value)}
                    />
                    <span>%</span>
                  </div>
                </div>

                <div className="tp-row">
                  <div className="tp-input">
                    <span className="tp-label">TP2</span>
                    <input
                      type="number"
                      placeholder="Price"
                      value={inputs.tp2Price}
                      onChange={(e) => handleInputChange('tp2Price', e.target.value)}
                    />
                  </div>
                  <div className="tp-percent">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={inputs.tp2Percent}
                      onChange={(e) => handleInputChange('tp2Percent', e.target.value)}
                    />
                    <span>%</span>
                  </div>
                </div>

                <div className="tp-row">
                  <div className="tp-input">
                    <span className="tp-label">TP3</span>
                    <input
                      type="number"
                      placeholder="Price"
                      value={inputs.tp3Price}
                      onChange={(e) => handleInputChange('tp3Price', e.target.value)}
                    />
                  </div>
                  <div className="tp-percent">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={inputs.tp3Percent}
                      onChange={(e) => handleInputChange('tp3Percent', e.target.value)}
                    />
                    <span>%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="results-section">
              {rrCalc ? (
                <>
                  <div className="rr-summary">
                    <div className="rr-stat risk">
                      <span className="label">Risk</span>
                      <span className="value">{rrCalc.riskPercent}%</span>
                      {parseFloat(inputs.positionSize) > 0 && (
                        <span className="amount">-${rrCalc.maxLoss}</span>
                      )}
                    </div>
                    <div className="rr-divider">:</div>
                    <div className="rr-stat reward">
                      <span className="label">Avg R:R</span>
                      <span className="value">{rrCalc.avgRR}</span>
                      {parseFloat(inputs.positionSize) > 0 && (
                        <span className="amount">+${rrCalc.totalPnl}</span>
                      )}
                    </div>
                  </div>

                  {rrCalc.targets.length > 0 && (
                    <div className="tp-results">
                      {rrCalc.targets.map((tp, idx) => (
                        <div key={idx} className="tp-result-item">
                          <div className="tp-header">
                            <span className="tp-name">{tp.label}</span>
                            <span className="tp-close">{tp.percent}% close</span>
                          </div>
                          <div className="tp-details">
                            <div className="tp-detail">
                              <span className="label">Price</span>
                              <span className="value">${tp.price}</span>
                            </div>
                            <div className="tp-detail">
                              <span className="label">Move</span>
                              <span className="value positive">+{tp.rewardPercent}%</span>
                            </div>
                            <div className="tp-detail">
                              <span className="label">R:R</span>
                              <span className="value">{tp.rr}</span>
                            </div>
                            {parseFloat(tp.pnl) > 0 && (
                              <div className="tp-detail">
                                <span className="label">P&L</span>
                                <span className="value positive">+${tp.pnl}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {parseFloat(rrCalc.avgRR) < 1 && rrCalc.targets.length > 0 && (
                    <div className="risk-warning">
                      <AlertTriangle size={14} />
                      <p>Average R:R below 1 means risk exceeds potential reward</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="no-results">
                  <Target size={48} />
                  <p>Enter entry price and stop loss to calculate risk/reward</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PnLCalculator;
